/**
 * Quiz Settings Modal
 *
 * Modal wrapper for configuring quiz mode in forms.
 */

import { useState, useEffect } from 'react';
import { useBuilderStore } from '../../stores/builderStore';
import { QuizSettings } from './QuizSettings';
import type { QuizSettings as QuizSettingsType } from '../../types/form';

interface QuizSettingsModalProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function QuizSettingsModal({ formId: _formId, isOpen, onClose }: QuizSettingsModalProps) {
  const { formSchema, updateSettings } = useBuilderStore();

  // Check if this is a quiz-type form (quiz mode was enabled at creation and should stay locked)
  const isQuizTypeForm = formSchema.settings.quiz?.enabled === true;

  const [settings, setSettings] = useState<QuizSettingsType>(
    formSchema.settings.quiz || { enabled: false }
  );

  // Update local state when schema changes
  useEffect(() => {
    if (formSchema.settings.quiz) {
      setSettings(formSchema.settings.quiz);
    }
  }, [formSchema.settings.quiz]);

  const handleSave = () => {
    updateSettings({ quiz: settings });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quiz Mode Settings</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            <QuizSettings settings={settings} onChange={setSettings} locked={isQuizTypeForm} />
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
